import { formatIsoDayFr, parseFrDay } from '../dateInput';
import { extractDueTime, mergeDueTime } from './taskReschedule';

export const DEFAULT_FICHE_DUE_TIME = '09:00';

export const HOUR_WHEEL: number[] = Array.from({ length: 24 }, (_, i) => i);
export const MINUTE_QUARTERS: number[] = [0, 15, 30, 45];

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function snapToQuarter(hhmm: string): string {
  const parsed = parseHhmm(hhmm) ?? DEFAULT_FICHE_DUE_TIME;
  const [hs, ms] = parsed.split(':');
  let hour = Number(hs);
  const minute = Number(ms);
  let q = Math.round(minute / 15) * 15;
  if (q === 60) {
    hour = (hour + 1) % 24;
    q = 0;
  }
  return `${pad2(hour)}:${pad2(q)}`;
}

export function splitDueTime(hhmm: string): { hour: number; minute: number } {
  const snapped = snapToQuarter(hhmm || DEFAULT_FICHE_DUE_TIME);
  const [hs, ms] = snapped.split(':');
  return { hour: Number(hs), minute: Number(ms) };
}

export function joinDueTime(hour: number, minute: number): string {
  const h = ((hour % 24) + 24) % 24;
  const m = MINUTE_QUARTERS.includes(minute) ? minute : 0;
  return `${pad2(h)}:${pad2(m)}`;
}

export function ficheDueTime(dueDatetime: string | null): string {
  return snapToQuarter(extractDueTime(dueDatetime) || DEFAULT_FICHE_DUE_TIME);
}

export function applyFicheDueDay(
  isoDay: string,
  hhmm: string | null,
): { due_date: string | null; due_datetime: string | null } {
  return mergeDueTime(
    { due_date: isoDay, due_datetime: null },
    isoDay,
    snapToQuarter(hhmm || DEFAULT_FICHE_DUE_TIME),
  );
}

export function formatFicheDueLabel(isoDay: string, hhmm: string): string {
  const day = formatIsoDayFr(isoDay);
  if (!day) return '';
  const time = snapToQuarter(parseHhmm(hhmm) ?? DEFAULT_FICHE_DUE_TIME);
  return `${day} · ${time}`;
}

export function parseFicheDueInput(raw: string): { day: string; time: string } | null {
  const t = raw.trim();
  if (!t) return null;
  const dotted = t.split('·').map((s) => s.trim());
  if (dotted.length === 2) {
    const day = parseFrDay(dotted[0] ?? '');
    const time = parseHhmm(dotted[1] ?? '');
    if (day && time) return { day, time: snapToQuarter(time) };
  }
  const spaced = t.match(/^(.+?)\s+(\S+)$/);
  if (spaced) {
    const day = parseFrDay(spaced[1] ?? '');
    const time = parseHhmm(spaced[2] ?? '');
    if (day && time) return { day, time: snapToQuarter(time) };
  }
  const day = parseFrDay(t);
  if (day) return { day, time: DEFAULT_FICHE_DUE_TIME };
  return null;
}

export function parseHhmm(raw: string): string | null {
  const t = raw.trim().toLowerCase().replace(/\s+/g, '');
  if (!t) return null;
  const match = t.match(/^(\d{1,2})(?:[:h](\d{1,2})?)?h?$/);
  if (!match) return null;
  const h = Number(match[1]);
  const m = match[2] === undefined || match[2] === '' ? 0 : Number(match[2]);
  if (!Number.isInteger(h) || !Number.isInteger(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function quarterHourTimes(): string[] {
  const out: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return out;
}
